import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, LogOut, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { SERVICES } from "@/data/services";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MAX_FILE_MB = 5;

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err, "Login failed. Check your email and password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-4" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm bg-white border border-border rounded-xl p-8 shadow-sm" data-testid="admin-login-form">
        <img src="/assets/logo.png" alt="Somoza's Landscaping logo" className="h-16 w-auto mx-auto" />
        <h1 className="mt-4 font-heading font-bold text-ink text-xl text-center">Owner Login</h1>
        <p className="text-sm text-[#4B5563] text-center mt-1">Manage photos and website content.</p>
        <div className="mt-6 flex flex-col gap-4">
          <div>
            <Label htmlFor="admin-email">Email</Label>
            <Input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="admin-password">Password</Label>
            <Input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="admin-login-error" role="alert">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-button"
            className="inline-flex items-center justify-center gap-2 bg-brand text-white font-semibold rounded-full px-6 py-3 hover:bg-brand-hover transition-colors disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
            Sign In
          </button>
          <a href="/" className="text-center text-sm text-[#4B5563] hover:text-brand transition-colors" data-testid="admin-back-to-site-link">
            ← Back to website
          </a>
        </div>
      </form>
    </div>
  );
}

function PhotoUpload({ categories, onUploaded }) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState(categories[0] || "Landscaping");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose a photo first.");
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`Photo must be ${MAX_FILE_MB} MB or smaller.`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("category", category);
      fd.append("description", description);
      fd.append("alt", description || `Somoza's Landscaping project - ${category}`);
      await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added to the gallery.");
      setFile(null);
      setDescription("");
      if (inputRef.current) inputRef.current.value = "";
      onUploaded();
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed."));
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-border rounded-xl p-6 flex flex-col gap-4" data-testid="admin-photo-upload-form">
      <h2 className="font-heading font-semibold text-ink text-lg">Add a Project Photo</h2>
      <div>
        <Label htmlFor="photo-file">Photo (max {MAX_FILE_MB} MB)</Label>
        <Input
          id="photo-file"
          ref={inputRef}
          data-testid="admin-photo-file-input"
          type="file"
          accept="image/*"
          required
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="mt-1.5"
        />
      </div>
      <div>
        <Label>Category</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="admin-photo-category-select" className="mt-1.5">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="photo-desc">Description (optional)</Label>
        <Input id="photo-desc" data-testid="admin-photo-description-input" maxLength={300} value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1.5" placeholder="e.g. New flower bed installation in Frisco" />
      </div>
      <button
        type="submit"
        disabled={uploading}
        data-testid="admin-photo-upload-button"
        className="inline-flex items-center justify-center gap-2 bg-brand text-white font-semibold rounded-full px-6 py-3 hover:bg-brand-hover transition-colors disabled:opacity-60"
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Upload className="w-4 h-4" aria-hidden="true" />}
        {uploading ? "Uploading…" : "Upload Photo"}
      </button>
    </form>
  );
}

function PhotosTab() {
  const [photos, setPhotos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ category: "", description: "", alt: "" });
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/gallery")
      .then((res) => {
        setPhotos(res.data.photos || []);
        setCategories(res.data.categories || []);
      })
      .catch((err) => toast.error(formatApiError(err, "Could not load photos.")));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (photo) => {
    setEditing(photo);
    setEditForm({ category: photo.category, description: photo.description || "", alt: photo.alt || "" });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/gallery/${editing.id}`, editForm);
      toast.success("Photo updated.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Update failed."));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/gallery/${deleting.id}`);
      toast.success("Photo removed from the gallery.");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Delete failed."));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <PhotoUpload categories={categories} onUploaded={load} />
      <div className="lg:col-span-2">
        <h2 className="font-heading font-semibold text-ink text-lg mb-4">Gallery Photos ({photos.length})</h2>
        {photos.length === 0 ? (
          <p className="text-[#4B5563] text-sm" data-testid="admin-photos-empty">No photos yet — upload the first one.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="admin-photos-grid">
            {photos.map((photo) => (
              <div key={photo.id} className="bg-white border border-border rounded-xl overflow-hidden" data-testid={`admin-photo-card-${photo.id}`}>
                <img src={fileUrl(photo.storage_path)} alt={photo.alt || "Gallery photo"} className="w-full aspect-[4/3] object-cover" loading="lazy" />
                <div className="p-4">
                  <p className="text-xs uppercase tracking-wide text-brand font-semibold">{photo.category}</p>
                  <p className="text-sm text-ink mt-1 line-clamp-2">{photo.description || "No description"}</p>
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      data-testid={`admin-photo-edit-${photo.id}`}
                      onClick={() => openEdit(photo)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-forest hover:text-forest-hover transition-colors"
                    >
                      <Pencil className="w-4 h-4" aria-hidden="true" /> Edit
                    </button>
                    <button
                      type="button"
                      data-testid={`admin-photo-delete-${photo.id}`}
                      onClick={() => setDeleting(photo)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" aria-hidden="true" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent data-testid="admin-photo-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Photo</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div>
              <Label>Category</Label>
              <Select value={editForm.category} onValueChange={(v) => setEditForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="admin-edit-category-select" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-desc">Description</Label>
              <Textarea id="edit-desc" data-testid="admin-edit-description-input" rows={3} maxLength={300} value={editForm.description} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="edit-alt">Alt text (for accessibility &amp; SEO)</Label>
              <Input id="edit-alt" data-testid="admin-edit-alt-input" maxLength={200} value={editForm.alt} onChange={(e) => setEditForm((f) => ({ ...f, alt: e.target.value }))} className="mt-1.5" />
            </div>
            <button
              type="button"
              onClick={saveEdit}
              disabled={saving}
              data-testid="admin-edit-save-button"
              className="inline-flex items-center justify-center gap-2 bg-brand text-white font-semibold rounded-full px-6 py-3 hover:bg-brand-hover transition-colors disabled:opacity-60"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              Save Changes
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent data-testid="admin-photo-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this photo?</AlertDialogTitle>
            <AlertDialogDescription>
              It will be removed from the public gallery. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-delete-confirm-button" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const CONTENT_FIELDS = [
  { key: "business_hours", label: "Business Hours", placeholder: "e.g. Mon–Sat 7:00 AM – 6:00 PM" },
  { key: "service_area", label: "Service Area", placeholder: "e.g. Dallas / DFW metro area" },
  { key: "contact1_name", label: "Contact 1 — Name" },
  { key: "contact1_role", label: "Contact 1 — Role" },
  { key: "contact1_phone", label: "Contact 1 — Phone" },
  { key: "contact1_email", label: "Contact 1 — Email" },
  { key: "contact2_name", label: "Contact 2 — Name" },
  { key: "contact2_role", label: "Contact 2 — Role" },
  { key: "contact2_phone", label: "Contact 2 — Phone" },
  { key: "contact2_email", label: "Contact 2 — Email" },
  { key: "notification_emails", label: "Estimate Notification Emails (comma-separated)", placeholder: "email1@example.com, email2@example.com" },
];

function ContentTab() {
  const [content, setContent] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content")
      .then((res) => {
        setContent(res.data);
        setOverrides(res.data.service_overrides || {});
      })
      .catch((err) => toast.error(formatApiError(err, "Could not load content.")));
  }, []);

  if (!content) return <p className="text-[#4B5563] text-sm">Loading…</p>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", { ...content, service_overrides: overrides });
      toast.success("Website content updated.");
    } catch (err) {
      toast.error(formatApiError(err, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-border rounded-xl p-6 flex flex-col gap-5 max-w-3xl" data-testid="admin-content-form">
      <h2 className="font-heading font-semibold text-ink text-lg">Site Content</h2>
      <div>
        <Label htmlFor="ct-about">About Us Text</Label>
        <Textarea
          id="ct-about"
          data-testid="admin-content-about"
          rows={4}
          value={content.about_text || ""}
          onChange={(e) => setContent((c) => ({ ...c, about_text: e.target.value }))}
          className="mt-1.5"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {CONTENT_FIELDS.map((field) => (
          <div key={field.key}>
            <Label htmlFor={`ct-${field.key}`}>{field.label}</Label>
            <Input
              id={`ct-${field.key}`}
              data-testid={`admin-content-${field.key.replace(/_/g, "-")}`}
              value={content[field.key] || ""}
              placeholder={field.placeholder || ""}
              onChange={(e) => setContent((c) => ({ ...c, [field.key]: e.target.value }))}
              className="mt-1.5"
            />
          </div>
        ))}
      </div>
      <details className="border border-border rounded-lg p-4">
        <summary className="font-heading font-semibold text-ink cursor-pointer" data-testid="admin-service-overrides-toggle">
          Service Descriptions (optional overrides)
        </summary>
        <div className="mt-4 grid grid-cols-1 gap-4">
          {SERVICES.map((s) => (
            <div key={s.id}>
              <Label htmlFor={`svc-${s.id}`}>{s.name}</Label>
              <Textarea
                id={`svc-${s.id}`}
                data-testid={`admin-service-override-${s.id}`}
                rows={2}
                placeholder={s.description}
                value={overrides[s.id] || ""}
                onChange={(e) => setOverrides((o) => ({ ...o, [s.id]: e.target.value }))}
                className="mt-1.5"
              />
            </div>
          ))}
        </div>
      </details>
      <button
        type="button"
        onClick={save}
        disabled={saving}
        data-testid="admin-content-save-button"
        className="inline-flex items-center justify-center gap-2 bg-brand text-white font-semibold rounded-full px-6 py-3 hover:bg-brand-hover transition-colors disabled:opacity-60 self-start"
      >
        {saving && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
        Save Content
      </button>
    </div>
  );
}

function EstimatesTab() {
  const [estimates, setEstimates] = useState(null);

  useEffect(() => {
    api.get("/admin/estimates")
      .then((res) => setEstimates(res.data.estimates || []))
      .catch((err) => toast.error(formatApiError(err, "Could not load estimates.")));
  }, []);

  if (estimates === null) return <p className="text-[#4B5563] text-sm">Loading…</p>;
  if (estimates.length === 0) {
    return <p className="text-[#4B5563] text-sm" data-testid="admin-estimates-empty">No estimate requests yet.</p>;
  }

  return (
    <div className="flex flex-col gap-4" data-testid="admin-estimates-list">
      {estimates.map((est) => (
        <article key={est.id} className="bg-white border border-border rounded-xl p-5" data-testid={`admin-estimate-${est.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-heading font-semibold text-ink">{est.name}</h3>
            <span className="text-xs text-[#9CA3AF]">{new Date(est.created_at).toLocaleString()}</span>
          </div>
          <p className="text-sm text-brand font-medium mt-0.5">{est.service || "General"}</p>
          <div className="mt-2 text-sm text-[#4B5563] flex flex-wrap gap-x-5 gap-y-1">
            <a href={`tel:${est.phone.replace(/[^0-9+]/g, "")}`} className="hover:text-brand">{est.phone}</a>
            <a href={`mailto:${est.email}`} className="hover:text-brand break-all">{est.email}</a>
            {(est.address || est.city || est.zip_code) && (
              <span>{[est.address, est.city, est.zip_code].filter(Boolean).join(", ")}</span>
            )}
            {est.preferred_contact && <span>Prefers: {est.preferred_contact}</span>}
          </div>
          {est.description && <p className="mt-2 text-sm text-ink whitespace-pre-wrap">{est.description}</p>}
          {(est.photo_paths || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {est.photo_paths.map((p, i) => (
                <a key={p} href={fileUrl(p)} target="_blank" rel="noopener noreferrer" data-testid={`admin-estimate-photo-${est.id}-${i}`}>
                  <img src={fileUrl(p)} alt={`Customer project photo ${i + 1}`} className="w-20 h-20 object-cover rounded-lg border border-border" loading="lazy" />
                </a>
              ))}
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api.get("/auth/me").then((res) => setUser(res.data)).catch(() => setUser(null));
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(null);
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand animate-spin" aria-label="Loading" />
      </div>
    );
  }

  if (!user) return <LoginView onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-surface" data-testid="admin-dashboard">
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="Somoza's Landscaping logo" className="h-10 w-auto" />
            <span className="font-heading font-semibold text-ink text-sm hidden sm:inline">Owner Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-[#4B5563] hover:text-brand transition-colors" data-testid="admin-view-site-link">
              View Website
            </a>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
              Log Out
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="photos">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="photos" data-testid="admin-tab-photos">Photos</TabsTrigger>
            <TabsTrigger value="content" data-testid="admin-tab-content">Site Content</TabsTrigger>
            <TabsTrigger value="estimates" data-testid="admin-tab-estimates">Estimate Requests</TabsTrigger>
          </TabsList>
          <div className="mt-6">
            <TabsContent value="photos"><PhotosTab /></TabsContent>
            <TabsContent value="content"><ContentTab /></TabsContent>
            <TabsContent value="estimates"><EstimatesTab /></TabsContent>
          </div>
        </Tabs>
      </main>
    </div>
  );
}
